import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import SEOHead from '../../components/base/SEOHead';

interface FAQItem {
  category: string;
  question: string;
  answer: string;
}

export default function FAQ() {
  const navigate = useNavigate();

  const [activeCategory, setActiveCategory] = useState('all');
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  const categories = [
    { id: 'all', name: 'All Questions', icon: 'ri-question-line' },
    { id: 'account', name: 'Account', icon: 'ri-user-line' },
    { id: 'trading', name: 'Trading', icon: 'ri-exchange-line' },
    { id: 'mining', name: 'Mining', icon: 'ri-cpu-line' },
    { id: 'security', name: 'Security', icon: 'ri-shield-check-line' },
    { id: 'payments', name: 'Payments', icon: 'ri-wallet-line' }
  ];

  const faqs = [
    {
      question: 'What is PepCoin?',
      answer:
        'PepCoin is a revolutionary cryptocurrency designed for secure and fast transactions. It offers low fees, high scalability, and is built on advanced blockchain technology.',
      category: 'general',
    },
    {
      question: 'How do I create an account?',
      answer:
        'Click on the "Sign Up" button, enter your email, create a password, and verify your email address. You can also use our referral system to get bonus coins!',
      category: 'general',
    },
    {
      question: 'Is my wallet secure?',
      answer:
        'Yes! We use industry-standard encryption and multi-factor authentication to protect your assets. Your private keys are never stored on our servers.',
      category: 'security',
    },
    {
      question: 'How do I buy PepCoin?',
      answer:
        'You can buy PepCoin through our P2P marketplace, by using crypto transfers, or by participating in our mining program. All methods are secure and verified.',
      category: 'trading',
    },
    {
      question: 'What are the transaction fees?',
      answer:
        'Transaction fees vary based on the operation type. P2P trades have a 2% fee, crypto conversions have a 1% fee, and withdrawals have network-dependent fees.',
      category: 'trading',
    },
    {
      question: 'How long do withdrawals take?',
      answer:
        'Withdrawals are typically processed within 24-48 hours. The exact time depends on the withdrawal method and network congestion.',
      category: 'trading',
    },
    {
      question: 'What is the referral program?',
      answer:
        'Invite friends using your unique referral link. You earn 10% of their first transaction, and they get a 5% bonus on their first deposit!',
      category: 'rewards',
    },
    {
      question: 'How does mining work?',
      answer:
        'Our mining system allows you to earn PepCoins by contributing computing power. Sessions last 24 hours and rewards are distributed based on your contribution.',
      category: 'rewards',
    },
    {
      question: 'What is staking?',
      answer:
        'Staking allows you to lock your PepCoins for a fixed period to earn rewards. The longer you stake, the higher your APY percentage.',
      category: 'rewards',
    },
    {
      question: 'How do I verify my account (KYC)?',
      answer:
        'Go to Profile → KYC Verification and upload your ID documents. Verification typically takes 24-48 hours and unlocks higher transaction limits.',
      category: 'security',
    },
    {
      question: 'What should I do if I forget my password?',
      answer:
        'Click "Forgot Password" on the login page. Enter your email and we will send you a password reset link.',
      category: 'security',
    },
    {
      question: 'Can I cancel a transaction?',
      answer:
        'Once a transaction is confirmed on the blockchain, it cannot be reversed. Always double-check recipient addresses before sending.',
      category: 'trading',
    },
    {
      question: 'What is the AI Trading Robot?',
      answer:
        'Our AI Trading Robot uses advanced algorithms to execute arbitrage opportunities automatically. It analyzes market conditions and executes profitable trades on your behalf.',
      category: 'rewards',
    },
    {
      question: 'How do I contact support?',
      answer:
        'You can reach our support team through the "Contact" page, email us at support@pepcoin.com, or use the live chat feature in your dashboard.',
      category: 'general',
    },
    {
      question: 'What payment methods are accepted?',
      answer:
        'We accept bank transfers, crypto payments (BTC, ETH, USDT), and our P2P marketplace supports various local payment methods.',
      category: 'trading',
    },
  ];

  const filteredFAQs =
    activeCategory === 'all'
      ? faqs
      : faqs.filter((faq) => faq.category === activeCategory);

  const schemaData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": "PepCoin FAQ - Frequently Asked Questions",
    "description": "Find answers to common questions about PepCoin platform, trading, mining, security, and payments.",
    "url": "https://pepcoin.com/faq",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    })),
    "breadcrumb": {
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://pepcoin.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": "FAQ",
          "item": "https://pepcoin.com/faq"
        }
      ]
    }
  };

  return (
    <>
      <SEOHead 
        title="FAQ - Frequently Asked Questions | PepCoin Help Center"
        description="Find answers to common questions about PepCoin platform, trading, mining, security, and payments. Get help with account setup, KYC verification, and more."
        keywords="PepCoin FAQ, cryptocurrency help, trading questions, mining help, account support, KYC verification"
        schemaData={schemaData}
      />
      <div className="min-h-screen bg-black">
        <Navbar />
        <div className="pt-16 px-4 sm:px-6 pb-6">
          <div className="max-w-4xl mx-auto">
            {/* Add Navigation Header */}
            <div className="flex items-center justify-between mb-6 sm:mb-8">
              <div className="flex items-center gap-2 sm:gap-4">
                <button
                  onClick={() => navigate(-1)}
                  className="px-3 sm:px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs sm:text-sm hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-arrow-left-line mr-1 sm:mr-2"></i>
                  Back
                </button>
                <div>
                  <h1 className="text-xl sm:text-2xl font-bold text-white">
                    Frequently Asked Questions
                  </h1>
                  <p className="text-gray-400 text-sm">
                    Quick answers to common questions
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 sm:gap-3">
                <button
                  onClick={() => navigate('/help')}
                  className="px-3 sm:px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white text-xs sm:text-sm hover:bg-white/20 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-question-line mr-1 sm:mr-2"></i>
                  Help Center
                </button>
                <button
                  onClick={() => navigate('/contact')}
                  className="px-3 sm:px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white text-xs sm:text-sm hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-customer-service-line mr-1 sm:mr-2"></i>
                  Contact Support
                </button>
              </div>
            </div>

            {/* Original Content */}
            <div className="pt-24 pb-16 px-4">
              <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="text-center mb-12">
                  <h1 className="text-4xl md:text-5xl font-black text-white mb-4">
                    Frequently Asked Questions
                  </h1>
                  <p className="text-gray-400 text-lg max-w-2xl mx-auto">
                    Find answers to common questions about PepCoin platform,
                    trading, mining, and more.
                  </p>
                </div>

                {/* Category Filter */}
                <div className="flex flex-wrap gap-3 justify-center mb-12">
                  {categories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setActiveCategory(category.id)}
                      className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 cursor-pointer whitespace-nowrap ${
                        activeCategory === category.id
                          ? 'bg-gradient-to-r from-cyan-500 to-purple-600 text-white'
                          : 'bg-white/5 border border-white/10 text-gray-300 hover:bg-white/10'
                      }`}
                    >
                      <i className={`${category.icon} mr-2`}></i>
                      {category.name}
                    </button>
                  ))}
                </div>

                {/* FAQ List */}
                <div className="space-y-4">
                  {filteredFAQs.map((faq, index) => (
                    <div
                      key={index}
                      className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-xl overflow-hidden hover:border-cyan-500/30 transition-all duration-300"
                    >
                      <button
                        onClick={() =>
                          setOpenIndex(openIndex === index ? null : index)
                        }
                        className="w-full px-6 py-5 flex items-center justify-between text-left cursor-pointer"
                      >
                        <span className="text-white font-semibold pr-4">
                          {faq.question}
                        </span>
                        <i
                          className={`ri-arrow-${
                            openIndex === index ? 'up' : 'down'
                          }-s-line text-cyan-400 text-xl flex-shrink-0`}
                        ></i>
                      </button>

                      {openIndex === index && (
                        <div className="px-6 pb-5">
                          <div className="pt-3 border-t border-white/10">
                            <p className="text-gray-300 leading-relaxed">
                              {faq.answer}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Contact Support */}
                <div className="mt-12 bg-gradient-to-r from-cyan-500/10 to-purple-600/10 border border-cyan-500/30 rounded-2xl p-8 text-center">
                  <i className="ri-customer-service-line text-5xl text-cyan-400 mb-4"></i>
                  <h3 className="text-2xl font-bold text-white mb-3">
                    Still have questions?
                  </h3>
                  <p className="text-gray-300 mb-6">
                    Our support team is here to help you 24/7
                  </p>
                  <Link
                    to="/contact"
                    className="inline-flex items-center gap-2 px-8 py-3 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-lg text-white font-medium hover:scale-105 transition-all duration-300 cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-mail-line"></i>
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
